import React from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarHeart, CandlestickChart, Flower2, Heart, Lock, Mic, Sparkles, Sun,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  MEMORIAL_KEEPSAKES_ANNIVERSARY_COPY,
  MEMORIAL_KEEPSAKES_ANNIVERSARY_TITLE,
  MEMORIAL_KEEPSAKES_CERTIFICATE_FOOTER,
  MEMORIAL_KEEPSAKES_CERTIFICATE_TAGLINE,
  MEMORIAL_KEEPSAKES_CERTIFICATE_TITLE,
  MEMORIAL_KEEPSAKES_DIGITAL_EXPERIENCE,
  MEMORIAL_KEEPSAKES_FRAME_MODE_COPY,
  MEMORIAL_KEEPSAKES_FRAME_MODE_FUTURE,
  MEMORIAL_KEEPSAKES_FRAME_MODE_TITLE,
  MEMORIAL_KEEPSAKES_MEMORY_OF_DAY,
  MEMORIAL_KEEPSAKES_PLANNED_LABEL,
  MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_COPY,
  MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_PRIVACY,
  MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_TITLE,
  MEMORIAL_KEEPSAKES_VOICE_EMPTY,
} from '../../content/memorialKeepsakesContent';
import {
  formatStoryDuration,
  getFirstStoryAudioUrl,
  getStoryDurationSeconds,
  getStoryPrimaryTitle,
  storyHasVoice,
} from '../../utils/storyDisplay';

const EXPERIENCE_ICONS = {
  memory_of_day: Sun,
  voice: Mic,
  anniversary: CalendarHeart,
  tribute: Heart,
};

function PlannedBadge() {
  return (
    <span className="inline-flex rounded-full border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_96%)] px-2 py-0.5 text-[9px] font-medium uppercase tracking-wide text-[#8b7355]">
      {MEMORIAL_KEEPSAKES_PLANNED_LABEL}
    </span>
  );
}

export function LegacyLinkCertificatePreview({
  name,
  memorialTitle,
  redirectUrl,
  qrPreviewUrl,
  loadingQr,
  published,
  publishedAt,
}) {
  const activationLabel = published
    ? publishedAt
      ? new Date(publishedAt).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : 'Active'
    : 'Activation pending';

  return (
    <div
      className="rounded-xl border border-[hsl(38_36%_42%_/_0.25)] bg-gradient-to-b from-[hsl(43_32%_98%)] to-[hsl(42_28%_96%)] p-5 shadow-[inset_0_0_0_1px_hsl(43_48%_58%_/_0.08)]"
      data-testid="legacy-link-certificate-preview"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8b7355]">
        {MEMORIAL_KEEPSAKES_CERTIFICATE_TITLE}
      </p>
      <p className="mt-3 font-serif text-lg font-light text-[#1c1917]">{name}</p>
      <p className="mt-1 text-sm text-[#5c534a]">{memorialTitle}</p>

      <div className="mt-4 flex flex-wrap items-start gap-4 border-y border-[hsl(38_14%_88%)] py-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded border border-[hsl(38_14%_86%)] bg-white p-1">
          {loadingQr ? (
            <span className="text-[10px] text-muted-foreground">…</span>
          ) : qrPreviewUrl && published ? (
            <img src={qrPreviewUrl} alt="" className="h-full w-full object-contain" />
          ) : (
            <div className="flex flex-col items-center justify-center text-center">
              <span className="text-[9px] font-medium uppercase tracking-wide text-[#8b7355]">Activation pending</span>
              {!published ? (
                <span className="mt-1 text-[8px] leading-tight text-muted-foreground">QR placeholder</span>
              ) : null}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1 text-xs">
          <p className="font-medium text-[#3d3833]">Dynamic link</p>
          {published && redirectUrl ? (
            <p className="break-all font-mono text-[10px] text-[#5c534a]">{redirectUrl}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground">/q/:shortCode · assigned when live</p>
          )}
          <p className="pt-1 text-[#5c534a]">
            <span className="font-medium">Activation:</span> {activationLabel}
          </p>
        </div>
      </div>

      <p className="mt-3 text-xs italic text-[#5c534a]">{MEMORIAL_KEEPSAKES_CERTIFICATE_TAGLINE}</p>
      <p className="mt-2 text-[10px] tracking-wide text-[#8b7355]">{MEMORIAL_KEEPSAKES_CERTIFICATE_FOOTER}</p>
    </div>
  );
}

export function FeaturedVoiceMemorySection({ featuredStory, backendBase, addVoiceHref }) {
  const hasVoice = storyHasVoice(featuredStory);
  const audioUrl = hasVoice ? getFirstStoryAudioUrl(featuredStory, backendBase) : null;
  const title = featuredStory ? getStoryPrimaryTitle(featuredStory) : null;
  const duration = featuredStory ? formatStoryDuration(getStoryDurationSeconds(featuredStory)) : null;

  return (
    <div className="rounded-xl border border-[hsl(38_14%_86%)]/70 bg-white/80 p-4" data-testid="keepsake-voice-preview">
      <p className="flex items-center gap-2 text-sm font-medium text-[#1c1917]">
        <Mic className="h-4 w-4 text-[#8b7355]" aria-hidden />
        Featured voice memory
      </p>
      {hasVoice && audioUrl ? (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-[#5c534a]">{title}</p>
          {duration ? <p className="text-[11px] text-muted-foreground">{duration}</p> : null}
          <audio controls preload="none" className="w-full max-w-md" data-testid="keepsake-voice-audio">
            <source src={audioUrl} />
            Your browser does not support audio playback.
          </audio>
          <p className="text-[10px] text-muted-foreground">Tap play — audio does not auto-start.</p>
        </div>
      ) : (
        <p className="mt-2 text-sm leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_VOICE_EMPTY}</p>
      )}
      {addVoiceHref ? (
        <Button type="button" size="sm" variant="outline" className="mt-3 rounded-full" asChild>
          <Link to={addVoiceHref}>{hasVoice ? 'Manage voice memories' : 'Add a voice memory'}</Link>
        </Button>
      ) : null}
    </div>
  );
}

export function PublicPrivateNfcConceptCard() {
  return (
    <div className="rounded-xl border border-[hsl(38_14%_86%)]/70 bg-[hsl(42_28%_98%)]/90 p-4" data-testid="keepsake-public-private-nfc">
      <p className="flex items-center gap-2 text-sm font-medium text-[#1c1917]">
        <Lock className="h-4 w-4 text-[#8b7355]" aria-hidden />
        {MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_TITLE}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_COPY}</p>
      <p className="mt-2 text-xs text-[#5c534a]/90">{MEMORIAL_KEEPSAKES_PUBLIC_PRIVATE_NFC_PRIVACY}</p>
      <p className="mt-2">
        <PlannedBadge />
      </p>
    </div>
  );
}

export function DigitalExperiencePreviewSection() {
  return (
    <div className="space-y-3" data-testid="keepsake-digital-experience">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-[#1c1917]">
          <Sparkles className="h-4 w-4 text-[#8b7355]" aria-hidden />
          Digital experience
        </p>
        <PlannedBadge />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {MEMORIAL_KEEPSAKES_DIGITAL_EXPERIENCE.map((item) => {
          const Icon = EXPERIENCE_ICONS[item.id] || Flower2;
          return (
            <div
              key={item.id}
              className="rounded-lg border border-[hsl(38_14%_88%)] bg-white/70 px-3 py-3"
            >
              <p className="flex items-center gap-1.5 text-xs font-medium text-[#1c1917]">
                <Icon className="h-3.5 w-3.5 text-[#8b7355]" aria-hidden />
                {item.title}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-[#5c534a]">{item.copy}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MemoryOfTheDayPreviewBlock() {
  return (
    <div className="rounded-lg border border-dashed border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)]/60 px-3 py-3">
      <p className="text-xs font-medium text-[#1c1917]">Memory of the Day</p>
      <p className="mt-1 text-[11px] leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_MEMORY_OF_DAY}</p>
      <p className="mt-2">
        <PlannedBadge />
      </p>
    </div>
  );
}

export function AnniversaryRemembrancePreviewBlock() {
  return (
    <div className="rounded-lg border border-[hsl(38_14%_88%)] bg-white/60 px-3 py-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-[#1c1917]">
        <CandlestickChart className="h-3.5 w-3.5 text-[#8b7355]" aria-hidden />
        {MEMORIAL_KEEPSAKES_ANNIVERSARY_TITLE}
      </p>
      <p className="mt-1 text-[11px] leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_ANNIVERSARY_COPY}</p>
      <p className="mt-2">
        <PlannedBadge />
      </p>
    </div>
  );
}

export function FrameModePreviewNote() {
  return (
    <div className="rounded-lg border border-[hsl(38_14%_88%)] bg-[hsl(42_28%_98%)]/50 px-3 py-3">
      <p className="text-xs font-medium text-[#1c1917]">{MEMORIAL_KEEPSAKES_FRAME_MODE_TITLE}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_FRAME_MODE_COPY}</p>
      <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">{MEMORIAL_KEEPSAKES_FRAME_MODE_FUTURE}</p>
    </div>
  );
}
